import React, { useState, useRef, useEffect } from 'react';
import { Typography } from "@material-tailwind/react";
import { FiChevronDown, FiSearch } from 'react-icons/fi';

const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Select an option...",
    label,
    loading = false,
    className = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        const term = (searchTerm || '').toString().toLowerCase();
        setFilteredOptions(
            (options || []).filter(option => {
                if (!option) return false;
                const label = (option.label !== undefined && option.label !== null) ? String(option.label) : '';
                const val = (option.value !== undefined && option.value !== null) ? String(option.value) : '';
                return label.toLowerCase().includes(term) || val.toLowerCase().includes(term);
            })
        );
    }, [searchTerm, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleOptionClick = (option) => {
        onChange(option.value);
        setIsOpen(false);
        setSearchTerm('');
    };

    const selectedValue = (value && typeof value === 'object' && value.value !== undefined) ? value.value : value;
    const selectedOption = (options || []).find(opt => String(opt && opt.value) === String(selectedValue));

    const displayLabel = selectedOption ? selectedOption.label : (
        value && typeof value === 'object' ? value.label : (
            (typeof value === 'string' && value !== '') || (typeof value === 'number') ? String(value) : null
        )
    );

    return (
        <div className={`relative ${className}`}>
            {label && (
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                    {label}
                </Typography>
            )}

            <div ref={dropdownRef} className="relative">
                <button
                    type="button"
                    className="w-full bg-white border border-blue-gray-200 rounded-lg px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setIsOpen(!isOpen)}
                    disabled={loading}
                >
                    <div className="flex items-center justify-between">
                        <span className={displayLabel ? "text-blue-gray-900" : "text-blue-gray-400"}>
                            {displayLabel || placeholder}
                        </span>
                        <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                </button>

                {isOpen && (
                    <div className="absolute left-0 z-[9999] w-full mt-1 bg-white border border-blue-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                        {/* Search Input */}
                        <div className="p-2 border-b border-blue-gray-100">
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-gray-400 w-4 h-4" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="w-full pl-10 pr-3 py-2 text-sm border border-blue-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Search options..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Options List */}
                        <div className="max-h-48 overflow-y-auto">
                            {loading ? (
                                <div className="p-3 text-center text-blue-gray-400">
                                    Loading options...
                                </div>
                            ) : filteredOptions.length === 0 ? (
                                <div className="p-3 text-center text-blue-gray-400">
                                    {searchTerm ? 'No matching options found' : 'No options available'}
                                </div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-gray-50 focus:outline-none focus:bg-blue-gray-50 transition-colors duration-150 ${option.value === value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-blue-gray-900'
                                            }`}
                                        onClick={() => handleOptionClick(option)}
                                    >
                                        {option.label}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchableSelect;
